import React from 'react';

// In the main repo this asset already lives at:
//   src/combat/assets/combat_UI.png
// Since this component lives under src/combat/ui, the relative path is:
//   ../assets/combat_UI.png
import combatUI from '../assets/combat_UI.png';
import combatUIArmor from '../assets/combat_UI_armor.png';
import combatUIReflect from '../assets/combat_UI_reflect.png';
import combatUIBoth from '../assets/combat_UI_both.png';

type Props = {
  hp: number;
  sp: number;
  maxHp: number;
  maxSp: number;
  deckCount: number;
  discardCount: number;
  armor: number;
  reflectPct: number;
  spPulseTick?: number;
};

/**
 * Compare props for CombatUI. Because Board and other parents can re-render
 * frequently during combat animations, a memoized CombatUI component with
 * this comparator prevents unnecessary updates when the visible values
 * (HP, SP, deck/discard counts, armor, reflect, pulse tick) do not
 * actually change.
 */
function areCombatUiPropsEqual(prev: Props, next: Props) {
  return (
    prev.hp === next.hp &&
    prev.sp === next.sp &&
    prev.maxHp === next.maxHp &&
    prev.maxSp === next.maxSp &&
    prev.deckCount === next.deckCount &&
    prev.discardCount === next.discardCount &&
    prev.armor === next.armor &&
    prev.reflectPct === next.reflectPct &&
    prev.spPulseTick === next.spPulseTick
  );
}

/**
 * Bottom-center combat UI overlay.
 *
 * Uses the provided PNG as the visual frame and places numbers directly
 * onto its circles via absolute positioning.
 */
function CombatUI({
  hp,
  sp,
  maxHp,
  maxSp,
  deckCount,
  discardCount,
  armor,
  reflectPct,
  spPulseTick,
}: Props) {
  const hasArmor = armor > 0;
  const hasReflect = reflectPct > 0;

  const armorCap = Math.max(1, Math.floor(Number(maxHp) || 100));
  const reflectCap = armorCap;

  const centerOverlaySrc = hasArmor && hasReflect
    ? combatUIBoth
    : hasArmor
      ? combatUIArmor
      : hasReflect
        ? combatUIReflect
        : null;

  return (
    <div className="combatUI" aria-hidden="true">
      <img className="combatUIImage" src={combatUI} alt="" draggable={false} />

      <div className="combatUIOverlay">
        {/* Center overlay badge (armor / reflect / both). */}
        {centerOverlaySrc && (
          <img
            className="combatUICenterOverlayImage"
            src={centerOverlaySrc}
            alt=""
            draggable={false}
          />
        )}

        {/* HP / SP (top circles) */}
        <div className="combatUIStat combatUIHp" data-feedback-anchor="playerHp" title="HP">
          {hp}
        </div>
        <div
          className={[
            'combatUIStat',
            'combatUISp',
            spPulseTick ? 'spPulse' : '',
          ].filter(Boolean).join(' ')}
          data-feedback-anchor="playerSp"
          title="SP"
          key={spPulseTick}
        >
          {sp}
        </div>

        {/* Deck / Discard (small white circles) */}
        <div className="combatUIStat combatUIDeck" data-draw-deck-anchor="true" title="Deck">
          {deckCount}
        </div>
        <div className="combatUIStat combatUIDiscard" data-lunge-discard-anchor="true" title="Discard">
          {discardCount}
        </div>

        {/* Armor / Reflect values (positioned on top of the overlay badge). */}
        {hasArmor && !hasReflect && (
          <div className="combatUICenterStat combatUICenterSingle" data-feedback-anchor="playerArmor" title="Armor">
            {armor}
          </div>
        )}

        {hasReflect && !hasArmor && (
          <div className="combatUICenterStat combatUICenterSingle" title="Reflect">
            {reflectPct}%
          </div>
        )}

        {hasArmor && hasReflect && (
          <>
            <div className="combatUICenterStat combatUIArmor" data-feedback-anchor="playerArmor" title="Armor">
              {armor}
            </div>
            <div className="combatUICenterStat combatUIReflect" title="Reflect">
              {reflectPct}%
            </div>
          </>
        )}
      </div>

      <div className="combatUIHoverZone" />
    </div>
  );
}

// Memoize CombatUI with a custom comparator so that Board rerenders do not
// trigger unnecessary updates to the static combat HUD when nothing has
// changed. The comparator checks all visible numeric props for equality.
export default React.memo(CombatUI, areCombatUiPropsEqual);
